/**
 * Rail description content component.
 * Displays detailed information about the currently focused asset in a rail,
 * including title, channel info, date, synopsis, and score.
 */
import BottomInfo from '../../elements/description/descriptionBottomInfo';
import Container from '../../atoms/container/descriptionContentContainer';
import Content from '@shared/types/enums/content';
import MainContext from '../../../../shared/components/utils/context/mainContext';
import MiddleInfo from '../../elements/description/descriptionMiddleInfo';
import Synopsis from '../../atoms/text/descriptionContentSynopsis';
import Title from '../../atoms/text/descriptionContentTitle';
import TopInfo from '../../elements/description/descriptionTopInfo';
import useAssetInfo from '@shared/components/utils/hooks/useAssetInfo';
import { useContext, useMemo } from 'react';

/**
 * Renders the description content for the focused asset in a rail.
 *
 * @returns {JSX.Element | null} Description content component or null if no asset
 */
export default function DescriptionContent() {
  // Hook
  const { display, props, synopsis, title } = useDescriptionContent();

  // Render
  return display ? (
    <Container>
      <TopInfo {...props.top} />
      <Title>{title}</Title>
      <MiddleInfo {...props.middle} />
      <Synopsis>{synopsis}</Synopsis>
      <BottomInfo {...props.bottom} />
    </Container>
  ) : null;
}

/**
 * Hook to get the description content of the currently focused asset in a rail.
 */
function useDescriptionContent() {
  // Context
  const { asset, hasFilters } = useContext(MainContext);

  //  Asset info
  const assetInfo = useAssetInfo({ asset, hasFilters });

  // Memoize the description content
  const { display, props, synopsis, title } = useMemo(() => {
    // Destructure asset info
    const {
      channelTitle,
      upgradablePlan,
      info,
      date,
      catchup,
      ageCode,
      commercialScoreText,
      displaySeparator,
      displayTip,
      synopsis,
      title,
    } = assetInfo;

    // Return memoized values
    return {
      display: !!asset,
      props: {
        top: {
          channelTitle,
          displayAI: asset?.type === Content.IA_CORNER,
          upgradablePlan,
          info,
        },
        middle: {
          date,
          catchup,
          ageCode,
        },
        bottom: {
          score: commercialScoreText,
          displaySeparator,
          displayTip,
        },
      },
      synopsis,
      title,
    };
  }, [asset, assetInfo]);

  // Return values
  return { display, props, synopsis, title };
}
